/**
 * Common definitions and functions related to communication with API.
 */

/**
 * TODO This "helpers" file will tend to grow fast at the future
 * Divide et impera
 */

import {HttpClient, HttpErrorResponse, HttpParams} from '@angular/common/http';
import {Moment} from 'moment';
import {Observable, of, throwError} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {CommonListFilterQueryParamsFactory} from '../factories/common-list-query-params.factory';
import {ListFilterFactory} from '../factories/list-filter.factory';
import {CommonListFilter} from '../models/filters/common-list-filter';
import {PaginationInfo} from '../models/pagination-info';
import {toApiDateTime} from './dates';

export const enum HTTP_STATUS {
    BAD_REQUEST          = 400,
    UNAUTHORIZED         = 401,
    FORBIDDEN            = 403,
    NOT_FOUND            = 404,
    UNPROCESSABLE_ENTITY = 422
}

export interface ResponseError {
    message: string;
    error: HttpErrorResponse;
}

/**
 * Common API error response handler.
 * @param error
 * @returns {Observable}
 */
export function handleResponseError(error: HttpErrorResponse): Observable<never> {
    let receivedMessage = error.error || error.message;
    receivedMessage = receivedMessage && receivedMessage.message || receivedMessage;

    const message = receivedMessage || (
        error.status
            ? `${error.status} - ${error.statusText}`
            : 'Server error'
    );

    const responseError: ResponseError = {
        message,
        error
    };

    console.error(message);

    return throwError(responseError);
}

/**
 * Generic definition of API's response with collection of items
 * and pagination info.
 */
export interface ListResponse<T> {
    data?: T[];
    listInfo?: PaginationInfo;
}

/**
 * Generic list data and pagination info extractor from API's response .
 * @param response {Response}
 * @returns {ListResponse<T>}
 */
export function extractListResponse<T>(response: ListResponse<T>): ListResponse<T> {
    return {
        data:     (response && response.data || []) as T[],
        listInfo: response.listInfo
    } as ListResponse<T>;
}

/**
 * Converts date to its string representation acceptable by API
 * @param dateTime {Date|Moment}
 * @returns {string}
 */
export function serializeDateTime(dateTime: Date | Moment): string {
    return toApiDateTime(dateTime);
}

export const createHttpParamsByListFilter = (listFilter?: Partial<CommonListFilter>): HttpParams | undefined =>
    listFilter
        ? new HttpParams({
            fromString: CommonListFilterQueryParamsFactory.createQueryParamsAsString(listFilter)
        })
        : undefined;

export const commonFetchListResponse = <T>(
    httpClient: HttpClient,
    url: string,
    listFilter?: CommonListFilter
): Observable<ListResponse<T>> => {
    const requestParams = createHttpParamsByListFilter(listFilter);

    return httpClient.get<ListResponse<T>>(url, {params: requestParams}).pipe(
        map(extractListResponse),
        catchError(handleResponseError)
    );
};

/**
 * Makes requests in series until get all available pages.
 * @template T
 * @param {(listFilter: CommonListFilter) => Observable<ListResponse<T>>} requestFactory
 * @param {CommonListFilter} [listFilter]
 * @returns {Observable<T[]>}
 */
export const fetchAllPages = <T>(
    requestFactory: (listFilter: CommonListFilter) => Observable<ListResponse<T>>,
    listFilter?: CommonListFilter
): Observable<T[]> => {
    const DEFAULT_MAX_PAGE_SIZE = 100;

    listFilter = listFilter
        ? {...listFilter}
        : ListFilterFactory.createWithSearchFields([]);

    listFilter.pageIndex = 0;
    listFilter.pageSize = Math.max(listFilter.pageSize, DEFAULT_MAX_PAGE_SIZE);
    listFilter.pageCount = 2;

    const doFetch = (pageListFilter: CommonListFilter, dataAcc: T[] = []): Observable<T[]> =>
        requestFactory(pageListFilter).pipe(
            map(extractListResponse),
            switchMap(response => {
                dataAcc.push(...response.data);

                return response.listInfo.pageCount > 1
                    ? doFetch({
                        ...pageListFilter,
                        pageIndex: pageListFilter.pageIndex + 1
                    }, dataAcc)
                    : of(dataAcc);
            })
        );

    return doFetch(listFilter);
};
